import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "./contexts/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import PreviewGate, { hasPreviewAccess } from "./components/PreviewGate";

// Eager-loaded: only the landing route so the first paint is instant.
import Home from "./pages/Home";

// Lazy-loaded route components — each becomes its own JS chunk.
const SeriesList = lazy(() => import("./pages/SeriesList"));
const SeriesDetail = lazy(() => import("./pages/SeriesDetail"));
const Watch = lazy(() => import("./pages/Watch"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Favorites = lazy(() => import("./pages/Favorites"));
const History = lazy(() => import("./pages/History"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Chat = lazy(() => import("./pages/Chat"));
const Profile = lazy(() => import("./pages/Profile"));
const TvGuide = lazy(() => import("./pages/TvGuide"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function RouteFallback() {
  return (
    <div className="flex justify-center items-center py-24" data-testid="route-loading">
      <Loader2 className="w-10 h-10 text-pink-500 animate-spin" strokeWidth={3} />
    </div>
  );
}

const TOAST_OPTIONS = {
  style: {
    border: "4px solid #0f172a",
    borderRadius: "16px",
    fontWeight: 700,
    boxShadow: "6px 6px 0 #0f172a",
  },
};

function App() {
  const [unlocked, setUnlocked] = useState(() => hasPreviewAccess());
  if (!unlocked) {
    return <PreviewGate onUnlock={() => setUnlocked(true)} />;
  }
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <main className="flex-1">
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/seriale" element={<SeriesList />} />
                <Route path="/grila-tv" element={<TvGuide />} />
                <Route path="/seriale/:id" element={<SeriesDetail />} />
                <Route path="/episod/:episodeId" element={<RouteErrorBoundary><Watch /></RouteErrorBoundary>} />
                <Route path="/autentificare" element={<Login />} />
                <Route path="/inregistrare" element={<Register />} />
                <Route path="/favorite" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
                <Route path="/istoric" element={<ProtectedRoute><History /></ProtectedRoute>} />
                <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
                <Route path="/profil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/confidentialitate" element={<Privacy />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <Toaster position="top-right" toastOptions={TOAST_OPTIONS} />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
